import api from '@btcdirect/shared/src/composables/api/config/communicator-config';
import { Endpoints } from '@btcdirect/shared/src/composables/api/endpoints';
import { createException } from '@btcdirect/shared/src/composables/api/exception/exception';
import { ApiErrorCodes, ApiRequestData, ApiResponseException } from '@btcdirect/shared/src/composables/api/types';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import axios, { AxiosRequestConfig } from 'axios';

const { debug } = useLogger();

/**
 * API communication layer to perform requests and parse the results.
 * @param endpoint
 * @param cancellable
 */
export const useApiCommunicator = (endpoint: Endpoints | string, cancellable: boolean = true, baseURL?: string) => {
    let abortController: AbortController | undefined;

    const get = async (payload?: ApiRequestData, replacements?: Record<string, string>): Promise<any> => {
        if (!endpoint) {
            return Promise.reject(createEndpointException());
        }

        const finalEndpoint = replaceEndpointVariables(endpoint, replacements);

        try {
            let config = payload ? { params: payload } : {};
            if (cancellable) {
                config = Object.assign(config, getCancelConfig());
            }

            if (baseURL) {
                config = Object.assign(config, { baseURL });
            }

            const result = await api.get(finalEndpoint, config);
            if (cancellable && !!result && !(result['promise'] instanceof Promise)) {
                abortController = undefined;
            }
            return result;
        } catch (exception) {
            return catchException(exception);
        }
    };

    const patch = async (payload?: ApiRequestData | ApiRequestData[], replacements?: Record<string, string>): Promise<any> => {
        if (!endpoint) {
            return Promise.reject(createEndpointException());
        }

        const finalEndpoint = replaceEndpointVariables(endpoint, replacements);

        try {
            const config = cancellable ? getCancelConfig() : {};

            if (baseURL) {
                config.baseURL = baseURL;
            }

            const result = await api.patch(finalEndpoint, payload, config);
            if (cancellable && !!result && !(result['promise'] instanceof Promise)) {
                abortController = undefined;
            }
            return result;
        } catch (exception) {
            return catchException(exception);
        }
    };

    const post = async (payload?: ApiRequestData | ApiRequestData[], replacements?: Record<string, string>): Promise<any> => {
        if (!endpoint) {
            return Promise.reject(createEndpointException());
        }

        const finalEndpoint = replaceEndpointVariables(endpoint, replacements);

        try {
            const config = cancellable ? getCancelConfig() : {};

            if (baseURL) {
                config.baseURL = baseURL;
            }

            const result = await api.post(finalEndpoint, payload, config);
            if (cancellable && !!result && !(result['promise'] instanceof Promise)) {
                abortController = undefined;
            }
            return result;
        } catch (exception) {
            return catchException(exception);
        }
    };

    function getCancelConfig(): AxiosRequestConfig {
        if (undefined !== abortController) {
            debug('Cancelling request as a new request has been started');
            abortController.abort();
        }

        const config = {} as AxiosRequestConfig;

        abortController = new AbortController();
        config.signal = abortController.signal;

        return config;
    }

    return {
        get,
        patch,
        post
    };
};

function replaceEndpointVariables(endpoint: Endpoints | string, replacements?: Record<string, string>): string {
    if (!replacements) {
        return endpoint;
    }

    let url = endpoint as string;

    Object.entries(replacements).forEach(([find, replacement]) => {
        url = url.replace(`{${find}}`, replacement);
    });

    return url;
}

function createEndpointException(): ApiResponseException {
    return createException('Cannot perform post request. No endpoint has been provided.', ApiErrorCodes.ER999, 400);
}

function catchException(exception: ApiResponseException): Promise<ApiResponseException> {
    if (axios.isCancel(exception)) {
        return Promise.reject(exception);
    }

    exception.isAuthTokenExpired = Object.values(exception.data.errors).some((error) => [ApiErrorCodes.ER701, ApiErrorCodes.ER801].includes(error.code));
    return Promise.reject(exception);
}
